import { __decorate } from "tslib";
import { Group, MathUtils, Vector3 } from 'three';
import { LabelTextConfig } from '../../Config/LabeltextConfig.js';
import { TibiaAnalyzerAPConfig as Config } from '../../Config/TibiaAnalyzerAPConfig.js';
import { CachedMeasure } from '../../Core/CachedMeasure.js';
import { Save, SaveChild } from '../../Core/Save.js';
import { ApexSign, ViewType } from '../../Models/AppModel.js';
import { SelectedApexMech } from '../../States/State.js';
import { Consts } from '../../Utils/Consts.js';
import { LimitUtils } from '../../Utils/LimitUtils.js';
import { Utils } from '../../Utils/Utils.js';
import { VectorUtils } from '../../Utils/VectorUtils.js';
import { CalculationPoint } from '../Primitive/CalculationPoint.js';
import { DraggablePoint } from '../Primitive/DraggablePoint.js';
import { Hexagon } from '../Primitive/Hexagon.js';
import { LabelAngle } from '../Primitive/Label/LabelAngle.js';
import { LabelText } from '../Primitive/Label/LabelText.js';
import { LimitLine } from '../Primitive/LimitLine.js';
import { LinePointToPoint2 } from '../Primitive/LinePointToPoint2.js';
export class MechanicalAxisTibiaAP extends Group {
    get angleForTemplating() {
        return VectorUtils.linesAngleFromOrigin(this.CP.position.clone().sub(this.CA.position), Consts.verDir);
    }
    get MPTAStandard() {
        return this._MPTAStandard;
    }
    set MPTAStandard(value) {
        this._MPTAStandard = value;
        this.update();
    }
    set MPTAStandardDegrees(value) {
        this._MPTAStandard = MathUtils.degToRad(value);
        this.update();
    }
    get LDTAStandard() {
        return this._LDTAStandard;
    }
    set LDTAStandard(value) {
        this._LDTAStandard = value;
        this.update();
    }
    set LDTAStandardDegrees(value) {
        this._LDTAStandard = MathUtils.degToRad(value);
        this.update();
    }
    get apexWithSign() {
        return this.apexAngle.value * this.apexAngleSign.value * (this._isLeft ? 1 : -1);
    }
    get proximalDirToCalculateApex() {
        return this.LP.position.clone().sub(this.MP.position).applyAxisAngle(this._axisForRotation, this._MPTAStandard);
    }
    get distalDirToCalculateApex() {
        return this.LA.position.clone().sub(this.MA.position).applyAxisAngle(this._axisForRotation, this._LDTAStandard);
    }
    get apexBisector() {
        return VectorUtils.getBisector(this.CP.position.clone().sub(this.apex.position), this.CA.position.clone().sub(this.apex.position));
    }
    get mechAxisUpperPoint() {
        return this.CP.position;
    }
    get mechAxisLowerPoint() {
        return this.CA.position;
    }
    constructor(isLeft, isContralateral, isEOC) {
        super();
        this.pointsToRegister = 4;
        this._limitLines = [];
        this._enableLimits = false; //refresh page to make it works (to improve performance when don't use it)
        this.onAfterRestore = () => {
            this.update();
        };
        this.name = 'TibiaAnalyzer';
        this._isContralateral = isContralateral;
        this._isLeft = isLeft;
        this._axisForRotation = isLeft ? Consts.planeNormal.clone() : Consts.planeNormal.clone().multiplyScalar(-1);
        this._isEOC = isEOC;
        if (this._enableLimits) {
            this._limitLines[0] = new LimitLine(ViewType.AP, 0xffff00);
            this._limitLines[1] = new LimitLine(ViewType.AP, 0x00ffff);
            this._limitLines[2] = new LimitLine(ViewType.AP, 0xff00ff);
            this._limitLines[3] = new LimitLine(ViewType.AP, 0xffffff);
            this._limitLines[4] = new LimitLine(ViewType.AP, 0xaaaa00);
            this._limitLines[5] = new LimitLine(ViewType.AP, 0x00aaaa);
            this._limitLines[6] = new LimitLine(ViewType.AP, 0xaa00aa);
            this._limitLines[7] = new LimitLine(ViewType.AP, 0xaaaaaa);
        }
        this.add(this.LP = new DraggablePoint('LP', ViewType.AP, Config.LP_color), this.MP = new DraggablePoint('MP', ViewType.AP, Config.MP_color), this.LA = new DraggablePoint('LA', ViewType.AP, Config.LA_color), this.MA = new DraggablePoint('MA', ViewType.AP, Config.MA_color), this.CP = new CalculationPoint('CP'), this.CA = new CalculationPoint('CA'), this.anatomicalAxis = new LinePointToPoint2('anatomicalAxis', ViewType.AP, Config.anatomicalAxis_color, this.CP, this.CA), this.LP_MP_Line = new LinePointToPoint2('LP_MP_Line', ViewType.AP, Config.LP_MP_Line_color, this.LP, this.MP), this.LA_MA_Line = new LinePointToPoint2('LA_MA_Line', ViewType.AP, Config.LA_MA_Line_color, this.LA, this.MA), this.MPTALabel = new LabelAngle({
            name: 'MPTALabel',
            arcA: this.MP,
            arcB: this.CA,
            arcCenter: this.CP,
            viewType: ViewType.AP,
            labelAnchor: new Vector3(isLeft ? -7 : 7, -5),
            labelParams: { backgroundColor: Config.MPTALabel_backgroundColor, fontColor: Config.MPTALabel_fontColor },
            labelErrorParams: { backgroundColor: 'rgba(255, 0, 0, 0.6)', fontColor: '#ffffff' },
            minAngle: Consts.paleyAngles.AP.MPTA.min,
            maxAngle: Consts.paleyAngles.AP.MPTA.max,
            textCallback: (angle) => `MPTA ${angle}°`
        }), this.LDTALabel = new LabelAngle({
            name: 'LDTALabel',
            arcA: this.LA,
            arcB: this.CP,
            arcCenter: this.CA,
            viewType: ViewType.AP,
            labelAnchor: new Vector3(isLeft ? 7 : -7, 5),
            labelParams: { backgroundColor: Config.LDTALabel_backgroundColor, fontColor: Config.LDTALabel_fontColor },
            labelErrorParams: { backgroundColor: 'rgba(255, 0, 0, 0.6)', fontColor: '#ffffff' },
            minAngle: Consts.paleyAngles.AP.LDTA.min,
            maxAngle: Consts.paleyAngles.AP.LDTA.max,
            textCallback: (angle) => `LDTA ${angle}°`
        }), ...this._limitLines);
        if (!this._isContralateral) {
            this.add(this.apex = new Hexagon(ViewType.AP, Config.apex_color), this.apexLabel = new LabelText({
                name: 'ApexLabel',
                color: LabelTextConfig.color,
                viewType: ViewType.AP,
                objToAnchor: this.apex,
                anchor: new Vector3(isLeft ? 7 : -7, 5),
                labelParams: { backgroundColor: Config.apexLabel_backgroundColor, fontColor: Config.apexLabel_fontColor, text: 'APEX' }
            }), this.proximalMechanicalAxis = new LinePointToPoint2('proximalMechanicalAxis', ViewType.AP, Config.proximalMechanicalAxis_color, this.CP, this.apex, 2, 1, 1.5), this.distalMechanicalAxis = new LinePointToPoint2('distalMechanicalAxis', ViewType.AP, Config.distalMechanicalAxis_color, this.CA, this.apex, 2, 1, 1.5));
        }
        this._MPTAStandard = MathUtils.degToRad(Consts.paleyAngles.AP.MPTA.standard);
        this._LDTAStandard = MathUtils.degToRad(Consts.paleyAngles.AP.LDTA.standard);
        this.bindEvents();
        this.MPTA = new CachedMeasure(() => {
            return this.MP.position.clone().sub(this.CP.position).angleTo(this.CA.position.clone().sub(this.CP.position));
        }, this.MP, this.CP, this.CA);
        this.LDTA = new CachedMeasure(() => {
            return this.LA.position.clone().sub(this.CA.position).angleTo(this.CP.position.clone().sub(this.CA.position));
        }, this.LA, this.CA, this.CP);
        this.length = new CachedMeasure(() => {
            return this.CA.position.distanceTo(this.CP.position);
        }, this.CA, this.CP);
        if (!this._isContralateral) {
            this.apexAngleToFix = new CachedMeasure(() => {
                return this.apex.visible ? this.getApexAngle() : 0;
            }, this.apex);
            this.apexAngle = new CachedMeasure(() => {
                return this.apexAngleToFix.value > Math.PI / 2 ? Math.PI - this.apexAngleToFix.value : this.apexAngleToFix.value;
            }, this.apex);
            this.apexAngleSign = new CachedMeasure(() => {
                if (this.apex.visible) {
                    const perpDir = this.apex.position.clone().sub(this.CP.position).cross(this._axisForRotation);
                    return Math.sign(this.apex.position.clone().sub(this.CA.position).dot(perpDir)) * (this.apexAngleToFix.value > Math.PI / 2 ? -1 : 1);
                }
                return ApexSign.valgus;
            }, this.apex);
        }
    }
    bindEvents() {
        this.bindEvent('onAfterDragMove', () => {
            this.update();
        });
        this.bindEvent('onDragEnd', () => {
            this.clearLimits();
        });
        this.bindDragLimit(this.LP, this.MP.position, this.LA.position, this.MA.position, true);
        this.bindDragLimit(this.MP, this.LP.position, this.MA.position, this.LA.position, true);
        this.bindDragLimit(this.LA, this.MA.position, this.LP.position, this.MP.position, false);
        this.bindDragLimit(this.MA, this.LA.position, this.MP.position, this.LP.position, false);
    }
    bindDragLimit(point, nearConsecutive, farConsecutive, diagonal, femurLimit) {
        point.bindEvent('onDragMove', (args) => {
            for (let i = 0; i < 2; i++) { //2 cycles removes projection multi limit errors
                LimitUtils.applyLimitAndProjectVector(nearConsecutive, diagonal, args.position, farConsecutive, point.scale.x, this._limitLines[i * 4 + 0]);
                LimitUtils.applyLimitAndProjectVector(diagonal, farConsecutive, args.position, nearConsecutive, point.scale.x, this._limitLines[i * 4 + 1]);
                LimitUtils.applyLimitAndProjectVector(nearConsecutive, farConsecutive, args.position, nearConsecutive.clone().add(nearConsecutive.clone().sub(diagonal)), point.scale.x, this._limitLines[i * 4 + 2]);
                femurLimit && this._LE && LimitUtils.applyLimitAndProjectVector(this._LE, this._ME, args.position, this.CA.position, point.scale.x, this._limitLines[i * 4 + 3]);
            }
        });
    }
    restorePositions(positions) {
        this.LP.position.copy(positions[0].setZ(0));
        this.MP.position.copy(positions[1].setZ(0));
        this.LA.position.copy(positions[2].setZ(0));
        this.MA.position.copy(positions[3].setZ(0));
        this.update();
    }
    initPositions(positions) {
        this.fixPositionsOrder(positions);
        this.LP.position.copy(positions[0].setZ(0));
        this.MP.position.copy(positions[1].setZ(0));
        this.LA.position.copy(positions[2].setZ(0));
        this.MA.position.copy(positions[3].setZ(0));
        this.update();
    }
    fixPositionsOrder(positions) {
        const middlePointA = positions[0].clone().lerp(positions[1], 0.5);
        const middlePointB = positions[2].clone().lerp(positions[3], 0.5);
        const perpDir = middlePointA.clone().sub(middlePointB).cross(Consts.planeNormal);
        const sign = this._isLeft ? -1 : 1;
        if (Math.sign(positions[0].clone().sub(middlePointA).dot(perpDir)) === sign) {
            Utils.swapItemInArray(0, 1, positions);
        }
        if (Math.sign(positions[2].clone().sub(middlePointA).dot(perpDir)) === sign) {
            Utils.swapItemInArray(2, 3, positions);
        }
    }
    update() {
        this.CA.position.copy(this.LA.position.clone().lerp(this.MA.position, 0.5));
        this.CP.position.copy(this.LP.position.clone().lerp(this.MP.position, 0.5));
        if (!this._isContralateral) {
            const MPTAValid = Utils.isRadiantsAngleInDegreesRange(this.MPTA.value, Consts.paleyAngles.AP.MPTA.min, Consts.paleyAngles.AP.MPTA.max);
            const LDTAValid = Utils.isRadiantsAngleInDegreesRange(this.LDTA.value, Consts.paleyAngles.AP.LDTA.min, Consts.paleyAngles.AP.LDTA.max);
            const invalidAngles = !LDTAValid || !MPTAValid;
            const apex = VectorUtils.lines2DIntersection(this.CP.position, this.CP.position.clone().add(this.proximalDirToCalculateApex), this.CA.position, this.CA.position.clone().add(this.distalDirToCalculateApex));
            apex && this.apex.position.copy(apex);
            this.setApexVisibility(invalidAngles);
        }
        this.dispatchEvent({ type: 'updated' });
    }
    setApexVisibility(visible) {
        if (this._isEOC && (this.selectedApex === SelectedApexMech.femurDistal || this.selectedApex === SelectedApexMech.femurProximal)) {
            visible = false;
        }
        if (this.apex.visible !== visible) {
            this.apex.visible = visible;
            this.apexLabel.visible = visible;
            this.proximalMechanicalAxis.visible = visible;
            this.distalMechanicalAxis.visible = visible;
        }
    }
    clearLimits() {
        for (const limitLine of this._limitLines) {
            limitLine.visible = false;
        }
    }
    setFemurLimits(LE, ME) {
        this._LE = LE;
        this._ME = ME;
    }
    /**
     * Calculates apex angle.
     */
    getApexAngle() {
        return this.CA.position.clone().sub(this.apex.position).angleTo(this.apex.position.clone().sub(this.CP.position));
    }
}
__decorate([
    Save('position')
], MechanicalAxisTibiaAP.prototype, "LP", void 0);
__decorate([
    Save('position')
], MechanicalAxisTibiaAP.prototype, "MP", void 0);
__decorate([
    Save('position')
], MechanicalAxisTibiaAP.prototype, "LA", void 0);
__decorate([
    Save('position')
], MechanicalAxisTibiaAP.prototype, "MA", void 0);
__decorate([
    SaveChild()
], MechanicalAxisTibiaAP.prototype, "MPTALabel", void 0);
__decorate([
    SaveChild()
], MechanicalAxisTibiaAP.prototype, "LDTALabel", void 0);
__decorate([
    SaveChild()
], MechanicalAxisTibiaAP.prototype, "apexLabel", void 0);
__decorate([
    Save()
], MechanicalAxisTibiaAP.prototype, "_MPTAStandard", void 0);
__decorate([
    Save()
], MechanicalAxisTibiaAP.prototype, "_LDTAStandard", void 0);
__decorate([
    Save()
], MechanicalAxisTibiaAP.prototype, "selectedApex", void 0);
//# sourceMappingURL=TibiaAnalyzerAP.js.map